import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import styles from './styles.module.scss';
import addVariants from '../utils/addVariants/index.ts';
import useSpacing from '../utils/useSpacing/index.ts';

export const VARIANTS = [
  'default',
  'center',
  'font-bold',
  'square',
  'no-padding',
  'half-padding',
  'athens-gray',
  'blue',
  'white',
  'shadow'
];
const SIDES = ['top', 'bottom', 'left', 'right'];

export const BORDER_TYPES = ['all', 'none', ...SIDES];
/**
 * This is meant to be a container to display information in a card format
 */
const Card = React.forwardRef(
  (
    {
      children,
      className,
      variant,
      border: rawBorder,
      width,
      spacing,
      style,
      component: Component,
      ...props
    },
    ref
  ) => {
    const border = [].concat(rawBorder);
    const variants = [].concat(variant);

    if (border.includes('all')) {
      variants.push('border-all');
    } else {
      variants.push('flat');
      if (!border.includes('none')) {
        border.forEach(b => {
          if (SIDES.includes(b)) {
            variants.push(`border-${b}`);
          }
        });
      }
    }

    const spacingStyle = useSpacing(spacing, {
      style: {
        width,
        ...style
      }
    });

    return (
      <Component
        className={classnames(addVariants('aglmd-card', styles, variants), className)}
        {...props}
        style={spacingStyle}
        ref={ref}
      >
        {children}
      </Component>
    );
  }
);

Card.propTypes = {
  /**
   * The width of the card
   */
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**
   * The locations of border(s) around the card
   */
  border: PropTypes.oneOfType([
    PropTypes.oneOf(BORDER_TYPES),
    PropTypes.arrayOf(PropTypes.oneOf(BORDER_TYPES))
  ]),
  /**
   * Additional classnames to apply
   */
  className: PropTypes.string,
  /**
   * Decides the variant of the Card
   */
  variant: PropTypes.oneOfType([
    PropTypes.oneOf(VARIANTS),
    PropTypes.bool,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.oneOf(VARIANTS), PropTypes.bool]))
  ]),
  /**
   * Allow customization of the margin around the `Card`. See `Spacer` Component
   */
  spacing: PropTypes.shape({
    top: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    bottom: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    left: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    right: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    x: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    y: PropTypes.oneOfType([PropTypes.number, PropTypes.bool])
  }),
  /**
   * Custom styles to apply, use sparingly
   */
  style: PropTypes.shape({}),
  /**
   * Inner content of the Card
   */
  children: PropTypes.node,
  /**
   * Render onto an element type
   */
  component: PropTypes.elementType
};

Card.defaultProps = {
  border: 'all',
  children: null,
  className: '',
  variant: 'default',
  width: '100%',
  style: {},
  spacing: {},
  component: 'div'
};

export default Card;
